import { ReactNode, useEffect, useRef, useState } from "react";

import classNames from "../../utils/classNames";

interface CollapsibleSectionProps {
  isOpen: boolean;
  children: ReactNode;
  className?: string;
  durationMs?: number;
}

export default function CollapsibleSection({
  isOpen,
  children,
  className = "",
  durationMs = 300,
}: CollapsibleSectionProps) {
  const contentRef = useRef<HTMLDivElement>(null);
  const [maxHeight, setMaxHeight] = useState("0px");

  useEffect(() => {
    const content = contentRef.current;
    if (!content) return;

    if (isOpen) {
      setMaxHeight(`${content.scrollHeight}px`);
      return;
    }

    // Set current height first so collapse animates from actual content size.
    setMaxHeight(`${content.scrollHeight}px`);
    requestAnimationFrame(() => setMaxHeight("0px"));
  }, [isOpen, children]);

  useEffect(() => {
    const content = contentRef.current;
    if (!content || !isOpen) return;

    const observer = new ResizeObserver(() => {
      setMaxHeight(`${content.scrollHeight}px`);
    });

    observer.observe(content);
    return () => observer.disconnect();
  }, [isOpen]);

  return (
    <div
      className={classNames(
        "overflow-hidden transition-[max-height,opacity] ease-in-out",
        isOpen ? "opacity-100" : "opacity-0 pointer-events-none",
        className
      )}
      style={{ maxHeight, transitionDuration: `${durationMs}ms` }}
    >
      <div ref={contentRef}>{children}</div>
    </div>
  );
}
